import { cn } from '@/shadcn/lib/utils';
import { BLOCK_TYPES } from '@/components/ui/richEditor/constants.richEditor';
import { ElementProps } from '@/components/ui/richEditor/elements/elements.richEditor.types';

import { MentionElement } from '@/components/ui/richEditor/elements/mention.element.richEditor';
import { ImageComponent } from '@/components/ui/richEditor/elements/image.element.richEditor';
import { LinkElement } from '@/components/ui/richEditor/elements/link.element.richEditor';

export const Element = (props: ElementProps) => {
  const {
    attributes,
    children,
    element,
    placeholder,
    subjectPlaceholder,
    withSubject = false,
    withMentions = false,
    readOnly = false,
    dataCy
  } = props;

  const style = {
    textAlign: element.align
  };
  const Heading1 = withSubject ? 'h2' : 'h1';
  const Heading2 = withSubject ? 'h3' : 'h2';

  if (element.isEmpty && readOnly) {
    return null;
  }

  // Subject specific case
  if (withSubject && element.type === BLOCK_TYPES.SUBJECT) {
    return (
      <h1
        data-placeholder={
          element.isEmpty && subjectPlaceholder && !readOnly
            ? subjectPlaceholder
            : undefined
        }
        aria-label={
          subjectPlaceholder && !readOnly ? subjectPlaceholder : undefined
        }
        className={cn(
          'border-border mb-2 border-b py-2 text-sm font-semibold',
          !readOnly &&
            'bg-card dark:bg-accent border-input sticky -top-2.5 z-1 -mx-2.5 -mt-2.5 mb-2.5 px-2.5',
          !readOnly &&
            element.isEmpty &&
            subjectPlaceholder &&
            'before:text-muted-foreground before:font-light first-of-type:only-of-type:before:absolute first-of-type:only-of-type:before:top-1/2 first-of-type:only-of-type:before:left-2.5 first-of-type:only-of-type:before:-translate-y-1/2 first-of-type:only-of-type:before:content-[attr(data-placeholder)]',
          readOnly && 'before:content-["Subject:_"]'
        )}
        style={style}
        {...attributes}
      >
        {children}
      </h1>
    );
  }
  // Mentions specific case
  if ((withMentions || readOnly) && element.type === BLOCK_TYPES.MENTION) {
    return (
      <MentionElement attributes={attributes} element={element}>
        {children}
      </MentionElement>
    );
  }
  switch (element.type) {
    case BLOCK_TYPES.BLOCKQUOTE:
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case BLOCK_TYPES.UNORDERED_LIST:
      return (
        <ul style={style} className="ml-6 list-disc" {...attributes}>
          {children}
        </ul>
      );
    case BLOCK_TYPES.HEADING1:
      return (
        <Heading1
          style={style}
          className="text-base font-semibold"
          {...attributes}
        >
          {children}
        </Heading1>
      );
    case BLOCK_TYPES.HEADING2:
      return (
        <Heading2
          style={style}
          className="text-base font-semibold"
          {...attributes}
        >
          {children}
        </Heading2>
      );
    case BLOCK_TYPES.LIST_ITEM:
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case BLOCK_TYPES.ORDERED_LIST:
      return (
        <ol style={style} className="ml-6 list-decimal" {...attributes}>
          {children}
        </ol>
      );
    case BLOCK_TYPES.LINK:
      return <LinkElement {...props} />;
    case BLOCK_TYPES.TABLE:
      return (
        <div className="border-input/90 scrollbar-thin my-2 w-full overflow-auto rounded-md border">
          <table className="w-full border-collapse">
            <tbody {...attributes}>{children}</tbody>
          </table>
        </div>
      );
    case BLOCK_TYPES.TABLE_ROW:
      return (
        <tr
          {...attributes}
          className={cn(
            'border-input/90 border-b transition-colors last:border-b-0',
            element.isSelected && 'bg-accent'
          )}
        >
          {children}
        </tr>
      );
    case BLOCK_TYPES.TABLE_CELL:
      return (
        <td
          style={style}
          className="border-input/90 border-r p-2.5 last:border-r-0"
          {...attributes}
        >
          {children}
        </td>
      );
    case BLOCK_TYPES.IMAGE:
      return <ImageComponent {...props} />;
    default:
      return (
        <p
          style={style}
          data-placeholder={
            element.isEmpty && placeholder && !readOnly
              ? placeholder
              : undefined
          }
          data-cy={dataCy}
          aria-label={placeholder && !readOnly ? placeholder : undefined}
          className={cn(
            element.isEmpty &&
              placeholder &&
              !readOnly &&
              `before:text-muted-foreground relative before:font-light first-of-type:only-of-type:before:absolute first-of-type:only-of-type:before:top-1/2 first-of-type:only-of-type:before:left-0 first-of-type:only-of-type:before:-translate-y-1/2 first-of-type:only-of-type:before:content-[attr(data-placeholder)]`
          )}
          {...attributes}
        >
          {children}
        </p>
      );
  }
};
